import axios from 'axios';
const adminURL = process.env.VUE_APP_ADMIN_URL;

export default class AuthService {

  /**
   * Authenticates with email and password.
   *
   * A correct password no longer yields a session. The backend returns a
   * short-lived MFA token plus a `nextStep` telling the caller whether the
   * administrator must register a device or just enter a code. The operational
   * token is written only by MfaService.verify().
   */
  static async login(email, password) {
    try {
      const response = await axios.post(`${adminURL}/useradmin/login`, { email, password });
      if (response.data.status === 'ok' && response.data.data.mfaToken) {
        localStorage.setItem('mfaToken', JSON.stringify(response.data.data.mfaToken));
        return { status: 'ok', nextStep: response.data.data.nextStep };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async logout() {
    try {
      localStorage.removeItem('accessToken');
      // A leftover enrollment token would otherwise survive sign-out.
      localStorage.removeItem('mfaToken');
      return { status: 'ok' };
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async getUserData() {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      if (accessToken) {
        const response = await axios.get(`${adminURL}/useradmin/me`, { headers: { 'Authorization': `Bearer ${accessToken}` } });
        if (response.data.status === 'ok') {
          return { status: 'ok', data: response.data.data };
        } else {
          return { status: 'error' };
        }
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      // A session refused for lack of a verified second factor must send the
      // administrator back to the right step, not to a generic failure.
      const code = error?.response?.data?.code ?? null;
      if (code === 'MFA_ENROLLMENT_REQUIRED') {
        return { status: 'error', redirectTo: '/mfa/setup' };
      }
      if (code === 'MFA_VERIFICATION_REQUIRED') {
        return { status: 'error', redirectTo: '/login' };
      }
      return { status: 'error' };
    }
  }

  /**
   * Saves the logged-in admin's own UI preferences (spec 028). The backend merges
   * them into `users-admin.preferences`, so keys not sent are kept.
   *
   * @param {{ defaultSection: string|null }} changes - `defaultSection` is a section
   *   key from `nav-sections.utils.js`, or `null` to clear the preference.
   * @returns {Promise<{ status: 'ok', data: { preferences: object } } | { status: 'error' }>}
   */
  static async updatePreferences(changes) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.patch(`${adminURL}/useradmin/me/preferences`, changes, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      }
      return { status: 'error' };
    } catch (error) {
      return { status: 'error' };
    }
  }

}
